import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { SUPERADMIN_ROLE } from '@app/common';
import type { ActiveBan } from '@app/common';
import { supabase } from '../lib/supabase';
import { onSessionExpired } from '../lib/sessionExpiry';
import { onAccountSuspended } from '../lib/accountSuspended';
import { POST_LOGIN_PATH } from '../constants/dashboard';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /**
   * Whether to show the admin menu. Read from the token's `app_metadata`, which
   * only the Supabase dashboard and the service-role key can write.
   *
   * This is presentation only — the API enforces the role itself on every admin
   * request. It also lags the dashboard: `app_metadata` is baked into the JWT at
   * issue time, so a newly promoted user has to sign out and back in before the
   * menu appears, even though the API would already accept them.
   */
  isSuperAdmin: boolean;
  /** The API rejected our token; the stored session is gone and the user must sign in again. */
  sessionExpired: boolean;
  /**
   * The account is suspended, with the reason and expiry the API returned. Unlike
   * `sessionExpired` this survives signing in again — Supabase refuses the login
   * outright while the ban is in force.
   */
  suspension: ActiveBan | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [suspension, setSuspension] = useState<ActiveBan | null>(null);
  // Read from the auth listener, which is registered once and would otherwise close
  // over stale state. One ref for both endings: either way the session is finished
  // and the listener must stop reacting to it.
  const frozenRef = useRef(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      // After expiry or suspension we keep the last session in state on purpose: the
      // sign-out below would otherwise null the user, and `RequireAuth` would replace
      // the page with /login before the dialog could insist on an acknowledgement.
      // Nothing here is usable — the stored session is already gone.
      if (frozenRef.current) return;
      setSession(newSession);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(
    () =>
      onAccountSuspended((ban) => {
        // Beats the expiry handler to the punch when both could fire: a suspension
        // explains itself, and "your session expired" would be a lie.
        if (frozenRef.current) return;
        frozenRef.current = true;
        setSuspension(ban);

        // Same reasoning as expiry — clear the stored session so another tab cannot
        // keep using it. Supabase will refuse a fresh login for as long as the ban runs.
        void supabase.auth.signOut();
      }),
    []
  );

  useEffect(
    () =>
      onSessionExpired(() => {
        // Requests in flight when the token died all 401 together; the first one wins.
        if (frozenRef.current) return;
        frozenRef.current = true;
        setSessionExpired(true);

        // Clear the stored session now rather than when the dialog's button is clicked,
        // so a second tab opened in the meantime finds nothing in localStorage and has
        // to sign in. auth-js ignores the logout endpoint's own 401 and wipes storage
        // regardless, which is exactly the case we are in here.
        void supabase.auth.signOut();
      }),
    []
  );

  const signInWithGoogle = useCallback(async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}${POST_LOGIN_PATH}` },
    });
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      isSuperAdmin: session?.user.app_metadata?.['role'] === SUPERADMIN_ROLE,
      sessionExpired,
      suspension,
      signInWithGoogle,
      signOut,
    }),
    [session, loading, sessionExpired, suspension, signInWithGoogle, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
